import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Modal} from 'antd';
import {useNavigate, useParams} from 'react-router-dom';
import NotFoundPage from '../NotFoundPage';
import {StyledTeamUserPage, StyledTeamUserPageSpace} from './StyledTeamUserPage';
import PageContainer from '../../components/PageContainer';
import SpinSmall from '../../components/SpinSmall';
import HeaderDetails from '../../components/pages/TeamUserPage/HeaderDetails';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader/BalancePageHeader';
import AuthenticationWindow from '../../components/pages/CardsPage/AuthenticationWindow';
import AddUserModal from '../../components/pages/TeamPage/AddUserModal/AddUserModal';
import UserSubscriptions from '../../components/pages/TeamUserPage/UserSubscriptions';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import DisableUserModal from '../../components/pages/TeamPage/DisableUserModal';
import routes from '../../routes/routes.json';
import {DEFAULT_MODAL_PROPS, SCAActionsConstants, userStatusesConstants} from '../../constants';
import {scaHelpers, systemHelpers, teamsHelpers} from '../../utils/helpers';
import {firebaseEvents} from '../../snippets/firebase';
import {alertActions} from '../../state/actions/alert.actions';
import {teamActions} from '../../state/actions/team.actions';

const {logEvent} = systemHelpers;

const {DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION, ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION} = SCAActionsConstants;
const {DISABLED, ACTIVE} = userStatusesConstants;

const TeamUserPage = ({
  employeeEmail,
  deleteEmployee,
  getEmployee,
  resendInvite,
  updateEmployee,
  updateEmployeeStatus,
}) => {
  const {email} = useParams();
  const [t] = useTranslation(['main', 'teams']);
  const [employee, setEmployee] = useState(null);
  const [pageLoading, setPageLoading] = useState({loading: true, isExist: false});
  const [authWindowProps, setAuthWindowProps] = useState({open: false});
  const [activeAction, setActiveAction] = useState('');
  const [terminateRelatedSubscriptions, setTerminateRelatedSubscriptions] = useState(false);
  const [isOpenDisableModal, setIsOpenDisableModal] = useState(false);
  const [editModalProps, setEditModalProps] = useState(DEFAULT_MODAL_PROPS);

  const subscriptionsCount = employee?.subscriptions_owned_qty || 0;

  const hasSubscriptions = subscriptionsCount > 0;

  const navigate = useNavigate();
  const finishLoading = (isExist = true) => setPageLoading({...pageLoading, isExist, loading: false});

  const handleStartLoading = () => setPageLoading({...pageLoading, loading: true});

  useEffect(() => {
    email && getUserInfo(email);
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  const getUserInfo = (email) => {
    if (!pageLoading.loading) handleStartLoading();
    getEmployee({
      email,
      successCallback: (employee) => {
        setEmployee(employee);
        finishLoading(true);
      },
      errorCallback: () => finishLoading(false)
    })
  }

  const handleErrorCallback = (response, validationKey) => {
    finishLoading();
    alertActions.error(response?.response?.data?.message || t(`teams:validation.${validationKey}`));
  }

  const getConfirmModalProps = (props) => teamsHelpers.getConfirmModalProps({t, employee, ...props})

  const getSCAProps = (action) => ({
    headers: scaHelpers.getAuthHeaders(action || activeAction),
    errorCallback: (response) => {
      scaHelpers.SCAResponseCallback({
        response,
        scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
        errorCallback: finishLoading
      });
    }
  });

  const handleOkEditModal = (data, successCallback, errorCallback) => {
    setEditModalProps({...editModalProps, loading: true});
    updateEmployee({
      email,
      data,
      successCallback: (response) => {
        setEmployee(response);
        handleCancelEditModal();
        logEvent(firebaseEvents.TEAM_USER_UPDATED);
        successCallback?.(response);
      },
      errorCallback: (response) => {
        setEditModalProps({...editModalProps, loading: false});
        errorCallback?.(response);
      }
    })
  }

  const handleDeleteUser = () => {
    Modal.confirm({
      ...getConfirmModalProps({action: 'delete'}),
      onOk() {
        setPageLoading({...pageLoading, loading: true});
        deleteEmployee({
          email,
          successCallback: () => {
            finishLoading();
            logEvent(firebaseEvents.TEAM_USER_DELETED);
            navigate(routes.team);
          },
          errorCallback: (response) => handleErrorCallback(response, 'failedToDeleteUser')
        })
      }
    });
  }

  const handleResendInvite = (action) => {
    Modal.confirm({
      ...getConfirmModalProps({action}),
      onOk() {
        setPageLoading({...pageLoading, loading: true});
        resendInvite({
          email,
          successCallback: () => {
            const updatedEmployee = teamsHelpers.getReInvitedUserData(employee);
            setEmployee(updatedEmployee);
            finishLoading();
            logEvent(firebaseEvents.TEAM_USER_INVITED);
          },
          errorCallback: (response) => handleErrorCallback(response, 'failedToResendInvitation')
        })
      }
    });
  }

  const handleDisableUser = () => setIsOpenDisableModal(true);

  const handleEnableUser = () => {
    Modal.confirm({
      ...getConfirmModalProps({action: 'enable'}),
      onOk() {
        setActiveAction(ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION);
        onEnableUser({action: ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION});
      }
    });
  }

  const handleUpdateUserStatus = ({
    bodyProps = {},
    headers,
    logEventName,
    status,
    errorCallback
  }) => {
    setPageLoading({...pageLoading, loading: true});
    updateEmployeeStatus({
      headers,
      email,
      data: {status, ...bodyProps},
      successCallback: () => {
        // update manually employee
        setEmployee({
          ...employee,
          account_status: status
        });
        finishLoading();
        logEvent(logEventName);
      },
      errorCallback: (response) => {
        errorCallback?.(response);
        handleErrorCallback(response, 'failedToUpdateUserStatus');
        finishLoading();
      }
    });
  }

  const onEnableUser = ({action} = {}) => {
    handleUpdateUserStatus({
      status: ACTIVE,
      logEventName: firebaseEvents.TEAM_USER_ENABLED,
      ...getSCAProps(action)
    });
  }

  const onDisableUser = ({action, terminate = undefined} = {}) => {
    const isTerminateUserSubscriptions = terminate !== undefined ? terminate : terminateRelatedSubscriptions;
    handleUpdateUserStatus({
      bodyProps: {is_terminate_user_subscriptions: isTerminateUserSubscriptions},
      logEventName: firebaseEvents.TEAM_USER_DISABLED,
      status: DISABLED,
      ...getSCAProps(action)
    });
  }

  const handleOnActionClick = ({action}) => {
    const actions = {
      disable: handleDisableUser,
      delete: handleDeleteUser,
      edit: handleOpenEditModal,
      enable: handleEnableUser,
      invite: () => handleResendInvite('invite'),
      'resend-invite': () => handleResendInvite('resend invitation')
    }
    actions.hasOwnProperty(action) && actions[action]();
  }

  const handleCancelEditModal = () => setEditModalProps({...editModalProps, loading: false, open: false});

  const handleOpenEditModal = () => setEditModalProps({...editModalProps, open: true});

  const handleCancelAuthModal = () => setAuthWindowProps({...authWindowProps, open: false});

  const handleCancelDisableModal = () => setIsOpenDisableModal(false);

  const handleOkDisableModal = (_, {terminate}) => {
    setTerminateRelatedSubscriptions(terminate);
    setIsOpenDisableModal(false);
    setActiveAction(DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION);
    onDisableUser({action: DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION, terminate});
  }

  const onSuccessAuth = () => {
    const actions = {
      [DISABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION]: onDisableUser,
      [ENABLE_USER_WITH_TREEZOR_ACCOUNT_ACTION]: onEnableUser,
    }
    handleCancelAuthModal();
    if (actions.hasOwnProperty(activeAction)) actions[activeAction]();
  }

  const getPageTitle = () => {
    let title = email;
    if (employee && employee.full_name) title = employee.full_name;
    return title;
  }

  const pageTitle = getPageTitle();
  const documentTitle = `${t('pageTitles.teamUserDetails')} ${pageTitle}`;

  const breadcrumbs = [
    {title: t('navigation.team'), href: routes.team},
    {title: pageTitle}
  ];

  const {isExist, loading} = pageLoading;

  return (
    <PageContainer>
      <PageDocumentDetails title={documentTitle} />
      <BalancePageHeader breadcrumbs={breadcrumbs} />
      <StyledTeamUserPage>
        <SpinSmall spinning={loading}>
          <StyledTeamUserPageSpace
            direction='vertical'
            size='large'
          >

            {isExist ? (
              <HeaderDetails
                data={employee}
                enableActions={email !== employeeEmail}
                onActionClick={handleOnActionClick}
              />
            ) : !loading && <NotFoundPage />}

            {hasSubscriptions && (
              <UserSubscriptions
                email={email}
                title={`${t('softwareUsage')} (${subscriptionsCount})`}
              />
            )}

          </StyledTeamUserPageSpace>
        </SpinSmall>

        <AuthenticationWindow
          {...authWindowProps}
          handleCancel={handleCancelAuthModal}
          onSuccess={onSuccessAuth}
          operationName={activeAction}
        />

        <DisableUserModal
          handleCancel={handleCancelDisableModal}
          handleOk={handleOkDisableModal}
          open={isOpenDisableModal}
        />

        <AddUserModal
          {...editModalProps}
          handleCancel={handleCancelEditModal}
          handleOk={handleOkEditModal}
          mode='edit'
          user={employee}
        />

      </StyledTeamUserPage>
    </PageContainer>
  );
}

const mapStateToProps = ({user: {employee}}) => ({
  employeeEmail: employee.email,
});

const mapDispatchToProps = {
  deleteEmployee: teamActions.deleteEmployee,
  getEmployee: teamActions.getEmployee,
  resendInvite: teamActions.resendInvite,
  updateEmployee: teamActions.updateEmployee,
  updateEmployeeStatus: teamActions.updateEmployeeStatus
}

export default connect(mapStateToProps, mapDispatchToProps)(TeamUserPage);
